import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { Observable, of, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SentryLogger } from '@fcom/core';
import { unsubscribe } from '@fcom/utils/unsubscribe';

import { ButtonMode, ButtonSize, ButtonTheme, ButtonType, IconPosition } from './enums';
import { ButtonIcon } from './interfaces';
import { LoaderTheme } from '../../loader';
import { AriaOptions } from '../../interfaces';

@Component({
  selector: 'fcom-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonComponent implements OnChanges, OnDestroy, AfterViewInit {
  @HostBinding('class.fill-mode') fillMode = false;
  @HostBinding('class.fill-mode-mobile') fillModeMobile = false;

  /**
   * The size of the button. 4 responsive & static sizes.
   */
  @Input() size: ButtonSize = ButtonSize.R_MEDIUM;

  /**
   * There are 3 themes available right now that renders different backgrounds/font colors and borders
   */
  @Input() theme: ButtonTheme = ButtonTheme.PRIMARY_BLUE;

  @Input() removeFromNavigation = false;

  /**
   * An icon to include, with its category, name and position
   */

  @Input() set icon(icon: ButtonIcon | null) {
    this._icon = icon;
    this.cdr.markForCheck();
  }

  get icon(): ButtonIcon | null {
    return this._icon;
  }

  /**
   * Whether the button should be disabled or not
   */
  @Input() disabled = false;

  /**
   * Whether the button should appear as disabled but be enabled
   */
  @Input() appearDisabled = false;

  /**
   * Disables the button and either replaces the current icon with the loading spinner. Or will add an icon to the right of text.
   */
  @Input() loading = false;

  /**
   * The pre-translated text to show
   */
  @Input() text: string;

  /**
   * Will show the button as a circle. Used with an icon-only button.
   */
  @Input() isCircle = false;

  /**
   * The aria tags that must be added to a button
   */
  @Input() aria: AriaOptions = {};

  /**
   * The type of button, for example "button" or "submit"
   */
  @Input() type: ButtonType = ButtonType.BUTTON;

  /**
   * The pre-translated hover text to display
   */
  @Input() title: string;

  /**
   * Do we fill the whole container or work as inline-block
   */
  @Input() mode: ButtonMode = ButtonMode.INLINE;

  @Input() focus$: Observable<boolean> = of(false);

  @ViewChild('buttonElement') buttonElement: ElementRef;

  /**
   * Event emitter for blur event
   */
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur = new EventEmitter<void>();

  public readonly IconPosition = IconPosition;
  public readonly ButtonSize = ButtonSize;
  public readonly ButtonTheme = ButtonTheme;
  public readonly ButtonMode = ButtonMode;
  public readonly LoaderTheme = LoaderTheme;

  private _icon: ButtonIcon | null;
  currentLoadingLabel: string;

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional() private logger?: SentryLogger
  ) {}

  private subscriptions: Subscription = new Subscription();

  ngOnChanges(changes: SimpleChanges): void {
    const mode = 'mode';

    if (changes[mode]) {
      this.fillMode = changes[mode].currentValue === ButtonMode.FILL;
      this.fillModeMobile = changes[mode].currentValue === ButtonMode.FILL_MOBILE;
    }

    if (this.loading) {
      if (!this.aria.loadingLabel) {
        this.logger?.warn('Missing aria label for loading button');
      }

      this.currentLoadingLabel = this.aria.loadingLabel;
    } else {
      this.currentLoadingLabel = this.aria.label;
    }

    /**
     * Detect if an icon only button hasn't got defined aria.label. In ngOnInit this.text is null
     * due to finLocalisation using the async pipe we would see false positives, so we do here
     * instead of ngOnInit.
     */
    if (!this.text && !changes.text && !this.aria.label) {
      this.logger?.warn('Missing aria label for icon-only button', this);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onClick(event: Event): void {
    if (this.disabled || this.loading || this.aria?.disabled) {
      event.stopPropagation();
    }
  }

  focus(): void {
    this.buttonElement?.nativeElement?.focus();
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(this.focus$.pipe(filter(Boolean)).subscribe(() => this.focus()));
  }
}
